/**
 * The class shell.
 *
 * Everything about a class used to render as one very long scroll. It is now a
 * layout: this component owns the single ClassDetailQuery, the header, and
 * every destructive dialog, and each section renders as its own route beneath
 * it (see components/Sidebar/contextNav for the section list). Keeping the query
 * here means moving between sections is a re-render, not a refetch.
 */
import { useState, useCallback, useMemo, useEffect } from 'react';
import { useParams, useOutletContext, Outlet } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Card, CardHeader, CardTitle } from '~/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import type { AppLayoutContext } from '~/types';
import { CLASS_SECTIONS, useContextNav } from '~/components/Sidebar/contextNav';
import { useWayBack } from '~/hooks/useWayBack';
import { useClassDetailQuery } from './useClassDetailQuery';
import {
  useRemoveStudentFromClass,
  useRemoveInstructor,
} from './useRosterMutations';
import AddStudentsDialog from './AddStudentsDialog';
import AssignInstructorDialog from './AssignInstructorDialog';
import type {
  RosterEntryFromQuery,
  InstructorFromQuery,
  ClassOfferingDetail,
  ClassDetailContext,
} from './classDetailTypes';

function ClassDetail() {
  const { schoolSlug, yearSlug, courseSlug, classOfferingId } = useParams();
  const { school, roleContext } = useOutletContext<AppLayoutContext>();
  const { setContextNav } = useContextNav();
  const isAdmin = roleContext?.selectedRole === 'PRIMARY_ADMIN' || roleContext?.selectedRole === 'ADMIN';

  const schoolYear = school?.schoolYears?.find((y) => y.slug === yearSlug);

  const { data, loading, refetch } = useClassDetailQuery(classOfferingId);
  const [removeStudent] = useRemoveStudentFromClass();
  const [removeInstructor] = useRemoveInstructor();

  const [dropConfirm, setDropConfirm] = useState<RosterEntryFromQuery | null>(null);
  const [removeInstructorConfirm, setRemoveInstructorConfirm] = useState<InstructorFromQuery | null>(null);
  const [addStudentsOpen, setAddStudentsOpen] = useState(false);
  const [assignInstructorOpen, setAssignInstructorOpen] = useState(false);

  const offering = data?.classOffering as ClassOfferingDetail | undefined;

  const basePath = `/app/${schoolSlug}/years/${yearSlug}/courses/${courseSlug}/classes/${classOfferingId}`;
  // Inside a class the sidebar is the class and nothing else, so this link is
  // the whole way out. It goes back to the screen the person came in from,
  // past any sections they clicked through. Opened directly, an administrator
  // goes up to the class's course and a teacher to My Teaching.
  const parentPath = isAdmin
    ? `/app/${schoolSlug}/years/${yearSlug}/courses/${courseSlug}`
    : `/app/${schoolSlug}/years/${yearSlug}/teaching`;
  const { goBack, toPreviousScreen } = useWayBack(basePath, parentPath);
  const courseName = data?.classOffering?.course?.name;
  const parentLabel = toPreviousScreen
    ? 'Back'
    : isAdmin ? `Back to ${courseName ?? 'the course'}` : 'Back to my teaching';

  const rosterEntries = useMemo(() => offering?.rosterEntries ?? [], [offering]);
  const activeEntries = useMemo(
    () => rosterEntries.filter((e) => e.state === 'ACTIVE'),
    [rosterEntries]
  );
  const droppedEntries = useMemo(
    () => rosterEntries.filter((e) => e.state !== 'ACTIVE'),
    [rosterEntries]
  );

  // Publish the class to the sidebar, which sits above this route's outlet and
  // so cannot read it any other way. Clearing on unmount is what makes the
  // class group disappear again when you navigate back out.
  const displayName = offering?.displayName ?? '';
  const subtitle = offering
    ? `${offering.term?.name ?? ''} · Section ${offering.section}`.replace(/^ · /, '')
    : '';
  useEffect(() => {
    if (!offering || !classOfferingId) return undefined;
    setContextNav({
      kind: 'class',
      title: displayName,
      subtitle,
      basePath,
      sections: CLASS_SECTIONS,
      backLink: { path: parentPath, label: parentLabel, go: goBack },
      exclusive: true,
    });
    return () => setContextNav(null);
  }, [offering, classOfferingId, displayName, subtitle, basePath, parentPath, parentLabel, goBack, setContextNav]);

  // Only ACTIVE roster entries block re-adding — dropped/transferred students
  // are reactivated by the backend when added again.
  const existingPersonIds = useMemo(
    () => new Set<string>(activeEntries.map((e) => e.person.id)),
    [activeEntries]
  );

  const existingInstructorIds = useMemo(
    () => new Set<string>((offering?.instructors ?? []).map((i) => i.person.id)),
    [offering]
  );

  const availablePeople = useMemo(
    () => (offering?.course?.schoolYear?.school?.staffMembers ?? []).map((sm) => ({
      id: sm.person.id,
      firstName: sm.person.firstName,
      lastName: sm.person.lastName,
    })),
    [offering]
  );

  const handleDropStudent = useCallback(async () => {
    if (!dropConfirm || !offering) return;
    await removeStudent({
      variables: { input: { classOfferingId: offering.id, personId: dropConfirm.person.id } },
    });
    setDropConfirm(null);
    await refetch();
  }, [dropConfirm, offering, removeStudent, refetch]);

  const handleRemoveInstructor = useCallback(async () => {
    if (!removeInstructorConfirm || !offering) return;
    await removeInstructor({
      variables: { input: { classOfferingId: offering.id, personId: removeInstructorConfirm.person.id } },
    });
    setRemoveInstructorConfirm(null);
    await refetch();
  }, [removeInstructorConfirm, offering, removeInstructor, refetch]);

  const openAddStudents = useCallback(() => setAddStudentsOpen(true), []);
  const openAssignInstructor = useCallback(() => setAssignInstructorOpen(true), []);

  const outletContext = useMemo<ClassDetailContext | null>(
    () => (offering
      ? {
        offering,
        isAdmin,
        refetch,
        activeEntries,
        droppedEntries,
        openAddStudents,
        openAssignInstructor,
        requestDropStudent: setDropConfirm,
        requestRemoveInstructor: setRemoveInstructorConfirm,
      }
      : null),
    [offering, isAdmin, refetch, activeEntries, droppedEntries, openAddStudents, openAssignInstructor]
  );

  if (!school) return <div className="p-6"><p>Loading...</p></div>;
  if (loading) return <div className="p-6"><p>Loading class...</p></div>;
  if (!offering || !outletContext) {
    return (
      <div className="p-6">
        <Card><CardHeader><CardTitle>Class Not Found</CardTitle></CardHeader></Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={goBack}
          aria-label={parentLabel}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h2 className="text-3xl font-bold">{offering.displayName}</h2>
          <p className="text-muted-foreground">
            {offering.course?.name} — {offering.term?.name} — Section {offering.section}
          </p>
        </div>
      </div>

      <Outlet context={outletContext} />

      {/* Add Students Dialog */}
      {schoolYear && isAdmin && (
        <AddStudentsDialog
          open={addStudentsOpen}
          onOpenChange={setAddStudentsOpen}
          classOfferingId={offering.id}
          schoolYearId={schoolYear.id}
          courseGradeLevels={offering.course?.gradeLevelKeys ?? []}
          existingPersonIds={existingPersonIds}
          capacity={offering.capacity}
          currentCount={activeEntries.length}
          onComplete={() => refetch()}
        />
      )}

      {/* Assign Instructor Dialog */}
      {school && isAdmin && (
        <AssignInstructorDialog
          open={assignInstructorOpen}
          onOpenChange={setAssignInstructorOpen}
          classOfferingId={offering.id}
          schoolId={school.id}
          existingInstructorIds={existingInstructorIds}
          availablePeople={availablePeople}
          onComplete={() => refetch()}
        />
      )}

      {/* Drop Student Confirmation */}
      {isAdmin && <AlertDialog open={!!dropConfirm} onOpenChange={() => setDropConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Drop Student</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to drop {dropConfirm?.person?.firstName} {dropConfirm?.person?.lastName} from this class?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDropStudent} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Drop Student
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>}

      {/* Remove Instructor Confirmation */}
      {isAdmin && <AlertDialog open={!!removeInstructorConfirm} onOpenChange={() => setRemoveInstructorConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Instructor</AlertDialogTitle>
            <AlertDialogDescription>
              Remove {removeInstructorConfirm?.person?.firstName} {removeInstructorConfirm?.person?.lastName} as instructor?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveInstructor}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>}
    </div>
  );
}

export default ClassDetail;
